// Accounts UI kit: the finance components on the dashboard's Foxbeep theme (see ./tokens).
// Same APIs as src/finance/ui/components.js; overlays share its Back-button stack.
// Responsive: on phones, tables become cards, modals/drawers become full-height sheets.
import { useState, useEffect, useRef, useMemo } from "react";
import { useMobile } from "../core";
import { useBackToClose } from "../../finance/ui/components";
import { T, text, surface, control, button, fmtDateTime } from "./tokens";
import { prepareUpload } from "../../lib/uploadCompression";
import { OptimizeNote } from "./OptimizedUpload";

// ─── StatusBadge ─────────────────────────────────────────────────────────────
const BADGE = {
    paid: ["Paid", T.success], finalized: ["Finalized", T.success], approved: ["Approved", T.info],
    pending: ["Pending", T.warning], outstanding: ["Outstanding", T.warning], overdue: ["Overdue", T.danger], draft: ["Draft", T.textFaint],
    review: ["Needs attention", T.warning], unverified: ["Unverified", T.warning], confirmed: ["Confirmed", T.success],
    current: ["Current", T.primary], legacy: ["Legacy", T.textFaint],
    upcoming: ["Scheduled", T.info],
    ready: ["Ready for review", T.primary], posted: ["Posted", T.success], reversed: ["Reversed", T.warning],
    unpaid: ["Unpaid", T.warning], partpaid: ["Part paid", T.warning], on: ["On", T.success], off: ["Off", T.textFaint],
};
export function StatusBadge({ status, label, title }) {
    const [def, color] = BADGE[status] || [status, T.textFaint];
    return (
        <span title={title} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 10px", borderRadius: T.radiusPill,
            fontSize: 12, fontWeight: 600, color, background: color + "1f", border: `1px solid ${color}33`, whiteSpace: "nowrap" }}>
            <span style={{ width: 6, height: 6, borderRadius: 3, background: color }} />
            {label || def}
        </span>
    );
}

// ─── Layout bits ─────────────────────────────────────────────────────────────
export function PageHeader({ title, subtitle, actions }) {
    const mobile = useMobile();
    return (
        <div style={{ display: "flex", flexDirection: mobile ? "column" : "row", alignItems: mobile ? "stretch" : "flex-end",
            justifyContent: "space-between", gap: 12, marginBottom: 24 }}>
            <div>
                <h2 style={text.pageTitle}>{title}</h2>
                {subtitle && <p style={text.pageSub}>{subtitle}</p>}
            </div>
            {actions && <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>{actions}</div>}
        </div>
    );
}

export function FinanceStatCard({ label, value, sub, tone }) {
    const mobile = useMobile();
    return (
        <div style={{ ...surface.card, padding: 16, minWidth: 0 }}>
            <div style={{ fontSize: 12, color: T.textMuted, fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
                {tone && <span style={{ width: 8, height: 8, borderRadius: 4, background: tone }} />}{label}
            </div>
            <div style={{ fontSize: mobile ? 18 : 22, fontWeight: 700, color: T.textStrong, marginTop: 6, fontVariantNumeric: "tabular-nums",
                overflowWrap: "anywhere", lineHeight: 1.2 }}>{value}</div>
            {sub && <div style={{ ...text.caption, marginTop: 4 }}>{sub}</div>}
        </div>
    );
}

export function StatGrid({ children }) {
    const mobile = useMobile();
    return <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr 1fr" : "repeat(4, minmax(0,1fr))", gap: mobile ? 10 : 16, marginBottom: 24 }}>{children}</div>;
}

export function Tabs({ value, onChange, items }) {
    return (
        <div role="tablist" style={{ display: "flex", gap: 4, padding: 4, background: T.surface2, border: `1px solid ${T.borderSoft}`,
            borderRadius: T.radius, width: "fit-content", maxWidth: "100%", overflowX: "auto", marginBottom: 16 }}>
            {items.map(([id, label, count]) => {
                const active = value === id;
                return (
                    <button key={id} role="tab" aria-selected={active} onClick={() => onChange(id)}
                        style={{ border: "none", cursor: "pointer", borderRadius: T.radiusSm, padding: "7px 14px", fontSize: 13, fontWeight: 600,
                            fontFamily: T.font, whiteSpace: "nowrap", background: active ? T.surface : "transparent",
                            color: active ? T.textStrong : T.textMuted, boxShadow: active ? T.shadow : "none" }}>
                        {label}{count !== undefined && <span style={{ marginLeft: 6, fontSize: 12, color: active ? T.primary : T.textFaint }}>{count}</span>}
                    </button>
                );
            })}
        </div>
    );
}

export function Notice({ tone = "warning", children, action }) {
    const color = tone === "danger" ? T.danger : tone === "info" ? T.primary : tone === "success" ? T.success : T.warning;
    return (
        <div style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "10px 12px", borderRadius: T.radiusSm,
            background: color + "14", border: `1px solid ${color}40`, color: T.text, fontSize: 13, lineHeight: 1.45 }}>
            <span style={{ width: 4, alignSelf: "stretch", borderRadius: 2, background: color, flexShrink: 0 }} />
            <div style={{ flex: 1 }}>{children}</div>
            {action}
        </div>
    );
}

export function EmptyState({ title, hint }) {
    return (
        <div style={{ padding: "40px 16px", textAlign: "center" }}>
            <div style={{ fontWeight: 600, color: T.textStrong }}>{title}</div>
            {hint && <div style={{ ...text.caption, marginTop: 6 }}>{hint}</div>}
        </div>
    );
}

// ─── Skeletons ──────────────────────────────────────────────────────────────
// Same shimmer as the dashboard's own loading screen (App.js SkeletonBlock): card2 → border → card2.
// Each skeleton is announced once as "Loading…" to screen readers; the blocks themselves are hidden.
let shimmerReady = false;
function ensureShimmer() {
    if (shimmerReady || typeof document === "undefined") return;
    const el = document.createElement("style");
    el.textContent = "@keyframes accSkeletonShimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}";
    document.head.appendChild(el);
    shimmerReady = true;
}
export function SkeletonBlock({ w = "100%", h = 14, radius = 6, mb = 0, style }) {
    ensureShimmer();
    return <div aria-hidden="true" style={{ width: w, height: h, borderRadius: radius, marginBottom: mb, flexShrink: 0,
        background: `linear-gradient(90deg, ${T.surface2} 25%, ${T.border} 50%, ${T.surface2} 75%)`,
        backgroundSize: "200% 100%", animation: "accSkeletonShimmer 1.4s infinite", ...style }} />;
}
function SkeletonFrame({ children, style }) {
    return (
        <div role="status" aria-busy="true" style={{ position: "relative", ...style }}>
            <span style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap" }}>Loading…</span>
            {children}
        </div>
    );
}
// Rows of a list or table inside a card (phones: stacked cards, as DataTable does).
export function SkeletonTable({ rows = 5, cols = 4, card = true }) {
    const mobile = useMobile();
    const widths = ["22%", "34%", "16%", "14%", "18%", "12%"];
    const body = mobile
        ? Array.from({ length: rows }, (_, i) => (
            <div key={i} style={{ ...surface.inset, padding: 12, display: "grid", gap: 8 }}>
                <SkeletonBlock w="55%" h={14} /><SkeletonBlock w="80%" h={11} /><SkeletonBlock w="35%" h={11} />
            </div>))
        : <>
            <div style={{ display: "flex", gap: 16, padding: "10px 14px", borderBottom: `1px solid ${T.border}` }}>
                {Array.from({ length: cols }, (_, c) => <SkeletonBlock key={c} w={widths[c % widths.length]} h={10} />)}
            </div>
            {Array.from({ length: rows }, (_, i) => (
                <div key={i} style={{ display: "flex", gap: 16, padding: "14px", borderBottom: i < rows - 1 ? `1px solid ${T.borderSoft}` : "none" }}>
                    {Array.from({ length: cols }, (_, c) => <SkeletonBlock key={c} w={widths[(c + i) % widths.length]} h={12} />)}
                </div>))}
        </>;
    return <SkeletonFrame style={card ? { ...surface.card, padding: mobile ? 10 : 0, display: mobile ? "grid" : "block", gap: 8, overflow: "hidden" } : { display: mobile ? "grid" : "block", gap: 8 }}>{body}</SkeletonFrame>;
}
// Figure tiles (summary strips, stat cards).
export function SkeletonTiles({ count = 4, columns }) {
    const mobile = useMobile();
    return (
        <SkeletonFrame style={{ display: "grid", gridTemplateColumns: columns || (mobile ? "1fr 1fr" : `repeat(${count}, 1fr)`), gap: 10 }}>
            {Array.from({ length: count }, (_, i) => (
                <div key={i} style={{ ...surface.card, padding: "12px 14px", display: "grid", gap: 8 }}>
                    <SkeletonBlock w="45%" h={10} /><SkeletonBlock w="70%" h={20} />
                </div>))}
        </SkeletonFrame>
    );
}
// Exact-size placeholder: the real element is laid out invisibly (so nothing moves when it appears) with the shimmer over it.
export function SkeletonShape({ children, radius = 8, style }) {
    return (
        <div aria-hidden="true" style={{ position: "relative", display: "inline-flex", ...style }}>
            <div style={{ visibility: "hidden", pointerEvents: "none" }}>{children}</div>
            <SkeletonBlock w="100%" h="100%" radius={radius} style={{ position: "absolute", inset: 0 }} />
        </div>
    );
}
// Table rows inside an existing <tbody> (Books).
export function SkeletonTableRows({ rows = 6, cols, cellStyle }) {
    return Array.from({ length: rows }, (_, i) => (
        <tr key={i} aria-hidden={i > 0 ? "true" : undefined}>
            {Array.from({ length: cols }, (_, c) => (
                <td key={c} style={cellStyle}>
                    {i === 0 && c === 0 && <span role="status" style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap" }}>Loading…</span>}
                    <SkeletonBlock w={["70%", "55%", "85%", "60%", "65%", "50%"][(c + i) % 6]} h={12} />
                </td>))}
        </tr>));
}
// A section card with a title and a few lines (forms, detail panels, drawers).
export function SkeletonCard({ lines = 4, title = true }) {
    return (
        <SkeletonFrame style={{ ...surface.card, padding: 18, display: "grid", gap: 10 }}>
            {title && <SkeletonBlock w="30%" h={16} mb={4} />}
            {Array.from({ length: lines }, (_, i) => <SkeletonBlock key={i} w={["90%", "75%", "60%", "82%", "68%"][i % 5]} h={12} />)}
        </SkeletonFrame>
    );
}

// ─── Forms ───────────────────────────────────────────────────────────────────
export function FormSection({ title, children }) {
    return (
        <fieldset style={{ border: "none", padding: 0, margin: "0 0 20px" }}>
            {title && <legend style={{ fontSize: 12, fontWeight: 700, color: T.textFaint, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 10, padding: 0 }}>{title}</legend>}
            <div style={{ display: "grid", gap: 14 }}>{children}</div>
        </fieldset>
    );
}

export function Field({ label, hint, error, required, children }) {
    return (
        <label style={{ display: "block" }}>
            <span style={text.label}>{label}{required && <span style={{ color: T.danger }}> *</span>}</span>
            {children}
            {error ? <span style={{ fontSize: 12, color: T.danger, marginTop: 4, display: "block" }}>{error}</span>
                : hint ? <span style={{ ...text.caption, marginTop: 4, display: "block" }}>{hint}</span> : null}
        </label>
    );
}

export const Input = (props) => <input {...props} style={{ ...control.input, ...(props.style || {}) }} />;
export const Select = ({ children, ...props }) => <select {...props} style={{ ...control.input, ...(props.style || {}) }}>{children}</select>;
export const TextArea = (props) => <textarea rows={3} {...props} style={{ ...control.input, resize: "vertical", minHeight: 72, ...(props.style || {}) }} />;
export function Button({ variant = "primary", size = "md", style, ...props }) {
    return <button {...props} style={{ ...button(variant, size), opacity: props.disabled ? 0.55 : 1, cursor: props.disabled ? "not-allowed" : "pointer", ...(style || {}) }} />;
}

// ─── Overlay: Modal (centered on desktop, bottom sheet on mobile) ────────────
function useEscape(open, onClose) {
    useEffect(() => {
        if (!open) return;
        const h = (e) => { if (e.key === "Escape") onClose?.(); };
        window.addEventListener("keydown", h);
        return () => window.removeEventListener("keydown", h);
    }, [open, onClose]);
}

export function Modal({ open, title, subtitle, onClose, footer, children, width = 520 }) {
    const mobile = useMobile();
    useEscape(open, onClose);
    useBackToClose(open, onClose);
    if (!open) return null;
    return (
        <div onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
            style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(15,23,42,.45)", display: "flex",
                alignItems: mobile ? "flex-end" : "center", justifyContent: "center", padding: mobile ? 0 : 20 }}>
            <div role="dialog" aria-modal="true" aria-label={title}
                style={{ ...surface.card, boxShadow: T.shadowPop, width: "100%", maxWidth: mobile ? "100%" : width,
                    maxHeight: mobile ? "94vh" : "90vh", display: "flex", flexDirection: "column",
                    borderRadius: mobile ? `${T.radius + 4}px ${T.radius + 4}px 0 0` : T.radius + 4 }}>
                <div style={{ padding: "18px 20px 12px", borderBottom: `1px solid ${T.borderSoft}`, display: "flex", gap: 12, alignItems: "flex-start" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 17, fontWeight: 700, color: T.textStrong }}>{title}</div>
                        {subtitle && <div style={{ ...text.caption, marginTop: 3 }}>{subtitle}</div>}
                    </div>
                    <button aria-label="Close" onClick={onClose} style={{ ...button("ghost", "sm"), minHeight: 32, padding: "4px 8px", fontSize: 18 }}>×</button>
                </div>
                <div style={{ padding: 20, overflowY: "auto", flex: 1 }}>{children}</div>
                {footer && (
                    <div style={{ padding: "12px 20px", paddingBottom: mobile ? "calc(12px + env(safe-area-inset-bottom))" : 12,
                        borderTop: `1px solid ${T.borderSoft}`, display: "flex", gap: 10, justifyContent: "flex-end",
                        flexDirection: mobile ? "column-reverse" : "row" }}>
                        {footer}
                    </div>
                )}
            </div>
        </div>
    );
}

// ─── Drawer (right panel on desktop, full screen on mobile) ──────────────────
export function Drawer({ open, title, subtitle, onClose, children, actions, width = 460 }) {
    const mobile = useMobile();
    useEscape(open, onClose);
    useBackToClose(open, onClose);
    if (!open) return null;
    return (
        <div onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
            style={{ position: "fixed", inset: 0, zIndex: 1900, background: "rgba(15,23,42,.35)", display: "flex", justifyContent: "flex-end" }}>
            <aside role="dialog" aria-modal="true" aria-label={title}
                style={{ background: T.surface, borderLeft: `1px solid ${T.border}`, width: mobile ? "100%" : width, maxWidth: "100%",
                    height: "100%", display: "flex", flexDirection: "column", boxShadow: T.shadowPop }}>
                <div style={{ padding: "16px 20px", borderBottom: `1px solid ${T.borderSoft}`, display: "flex", alignItems: "flex-start", gap: 12 }}>
                    <button aria-label="Close" onClick={onClose} style={{ ...button("ghost", "sm"), padding: "4px 8px", fontSize: 18 }}>{mobile ? "←" : "×"}</button>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 17, fontWeight: 700, color: T.textStrong }}>{title}</div>
                        {subtitle && <div style={{ ...text.caption, marginTop: 2 }}>{subtitle}</div>}
                    </div>
                    {actions}
                </div>
                <div style={{ padding: 20, overflowY: "auto", flex: 1, paddingBottom: "calc(20px + env(safe-area-inset-bottom))" }}>{children}</div>
            </aside>
        </div>
    );
}

export function DetailList({ items }) {
    return (
        <dl style={{ margin: 0, display: "grid", gap: 10 }}>
            {items.filter(Boolean).map(([k, v]) => (
                <div key={k} style={{ display: "flex", justifyContent: "space-between", gap: 16, fontSize: 14 }}>
                    <dt style={{ color: T.textMuted }}>{k}</dt>
                    <dd style={{ margin: 0, color: T.textStrong, textAlign: "right", fontVariantNumeric: "tabular-nums", minWidth: 0, overflowWrap: "anywhere" }}>{v}</dd>
                </div>
            ))}
        </dl>
    );
}

// ─── ••• action menu (popover on desktop, sheet on mobile) ───────────────────
export function ActionMenu({ items, label = "More actions" }) {
    const mobile = useMobile();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useBackToClose(open && mobile, () => setOpen(false));   // phone sheet: Back closes it
    useEffect(() => {
        if (!open || mobile) return;
        const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", h);
        return () => document.removeEventListener("mousedown", h);
    }, [open, mobile]);
    const visible = items.filter(Boolean);
    if (visible.length === 0) return null;
    const list = (
        <div role="menu" style={{ display: "grid", padding: 6 }}>
            {visible.map(it => (
                <button key={it.label} role="menuitem" disabled={it.disabled}
                    onClick={(e) => { e.stopPropagation(); setOpen(false); it.onClick?.(); }}
                    style={{ textAlign: "left", background: "transparent", border: "none", borderRadius: T.radiusSm, padding: mobile ? "14px 12px" : "9px 12px",
                        color: it.danger ? T.danger : T.text, fontSize: 14, cursor: it.disabled ? "not-allowed" : "pointer", opacity: it.disabled ? 0.5 : 1, fontFamily: T.font }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = T.surface2; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
                    {it.label}
                </button>
            ))}
        </div>
    );
    return (
        <div ref={ref} style={{ position: "relative", display: "inline-block" }} onClick={(e) => e.stopPropagation()}>
            <button aria-label={label} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(o => !o)}
                style={{ ...button("secondary", "sm"), padding: "6px 10px", minWidth: 36, letterSpacing: 1 }}>•••</button>
            {open && !mobile && (
                <div style={{ position: "absolute", right: 0, top: "calc(100% + 6px)", zIndex: 50, minWidth: 190, ...surface.card, boxShadow: T.shadowPop }}>{list}</div>
            )}
            {open && mobile && (
                <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 2100, background: "rgba(15,23,42,.4)", display: "flex", alignItems: "flex-end" }}>
                    <div onClick={(e) => e.stopPropagation()} style={{ ...surface.card, width: "100%", borderRadius: `${T.radius + 4}px ${T.radius + 4}px 0 0`, paddingBottom: "env(safe-area-inset-bottom)" }}>
                        {list}
                        <div style={{ padding: "0 12px 12px" }}><Button variant="secondary" style={{ width: "100%" }} onClick={() => setOpen(false)}>Cancel</Button></div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── DataTable: search, sort, pagination, mobile cards ───────────────────────
/**
 * columns: [{ key, header, render(row), sortValue(row), align, width, hideOnMobile, exportValue(row) }]
 * mobileCard(row): optional custom card renderer for phones
 * rowActions(row): node (primary button + ActionMenu)
 */
export function DataTable({ columns, rows, rowKey = (r) => r.id, searchText, searchPlaceholder = "Search…", filters, toolbarRight,
    pageSize = 10, defaultSort, onRowClick, rowActions, mobileCard, empty = "Nothing to show." }) {
    const mobile = useMobile();
    const [q, setQ] = useState("");
    const [sort, setSort] = useState(defaultSort || null);
    const [page, setPage] = useState(1);

    const filtered = useMemo(() => {
        const needle = q.trim().toLowerCase();
        let out = needle && searchText ? rows.filter(r => searchText(r).toLowerCase().includes(needle)) : rows;
        if (sort) {
            const col = columns.find(c => c.key === sort.key);
            const val = col?.sortValue || ((r) => r[sort.key]);
            out = [...out].sort((a, b) => {
                const va = val(a), vb = val(b);
                if (va === vb) return 0;
                if (va === null || va === undefined) return 1;
                if (vb === null || vb === undefined) return -1;
                return (va < vb ? -1 : 1) * (sort.dir === "desc" ? -1 : 1);
            });
        }
        return out;
    }, [rows, q, sort, columns, searchText]);

    useEffect(() => { setPage(1); }, [q, rows.length]);
    const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const cur = Math.min(page, pages);
    const slice = filtered.slice((cur - 1) * pageSize, cur * pageSize);

    const toggleSort = (c) => {
        if (!c.sortValue && c.sortable === false) return;
        setSort(s => (!s || s.key !== c.key ? { key: c.key, dir: "asc" } : s.dir === "asc" ? { key: c.key, dir: "desc" } : null));
    };

    return (
        <div style={{ ...surface.card, overflow: "hidden" }}>
            {(searchText || filters || toolbarRight) && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, padding: 12, borderBottom: `1px solid ${T.borderSoft}`, alignItems: "center" }}>
                    {searchText && <Input type="search" placeholder={searchPlaceholder} value={q} onChange={e => setQ(e.target.value)}
                        style={{ flex: mobile ? "1 1 100%" : "0 1 240px" }} />}
                    {filters}
                    {toolbarRight && <div style={{ marginLeft: mobile ? 0 : "auto", display: "flex", gap: 8 }}>{toolbarRight}</div>}
                </div>
            )}

            {slice.length === 0 ? <EmptyState title={q ? "No results" : empty} hint={q ? "Try a different search or clear the filters." : undefined} />
                : mobile ? (
                    <div>
                        {slice.map(r => (
                            <div key={rowKey(r)} onClick={onRowClick ? () => onRowClick(r) : undefined}
                                style={{ padding: "14px 14px", borderBottom: `1px solid ${T.borderSoft}`, cursor: onRowClick ? "pointer" : "default" }}>
                                {mobileCard ? mobileCard(r) : (
                                    <div style={{ display: "grid", gap: 6 }}>
                                        {columns.filter(c => !c.hideOnMobile).map(c => (
                                            <div key={c.key} style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 14 }}>
                                                <span style={{ color: T.textMuted }}>{c.header}</span>
                                                <span style={{ textAlign: "right" }}>{c.render ? c.render(r) : r[c.key]}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {rowActions && <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 10 }} onClick={e => e.stopPropagation()}>{rowActions(r)}</div>}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                            <thead>
                                <tr>
                                    {columns.map(c => {
                                        const sortable = !!c.sortValue;
                                        const active = sort?.key === c.key;
                                        return (
                                            <th key={c.key} onClick={sortable ? () => toggleSort(c) : undefined} aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : undefined}
                                                style={{ textAlign: c.align || "left", padding: "10px 16px", fontSize: 12, fontWeight: 600, color: T.textMuted,
                                                    borderBottom: `1px solid ${T.borderSoft}`, whiteSpace: "nowrap", width: c.width, cursor: sortable ? "pointer" : "default", userSelect: "none" }}>
                                                {c.header}{active ? (sort.dir === "asc" ? " ↑" : " ↓") : ""}
                                            </th>
                                        );
                                    })}
                                    {rowActions && <th style={{ borderBottom: `1px solid ${T.borderSoft}`, width: 1 }} />}
                                </tr>
                            </thead>
                            <tbody>
                                {slice.map(r => (
                                    <tr key={rowKey(r)} onClick={onRowClick ? () => onRowClick(r) : undefined}
                                        style={{ cursor: onRowClick ? "pointer" : "default" }}
                                        onMouseEnter={(e) => { e.currentTarget.style.background = T.surface2 + "88"; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
                                        {columns.map(c => (
                                            <td key={c.key} style={{ padding: "12px 16px", borderBottom: `1px solid ${T.borderSoft}`, textAlign: c.align || "left", color: T.text, verticalAlign: "middle", whiteSpace: c.nowrap ? "nowrap" : undefined }}>
                                                {c.render ? c.render(r) : r[c.key]}
                                            </td>
                                        ))}
                                        {rowActions && (
                                            <td style={{ padding: "8px 16px", borderBottom: `1px solid ${T.borderSoft}`, whiteSpace: "nowrap", textAlign: "right" }} onClick={e => e.stopPropagation()}>
                                                <div style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>{rowActions(r)}</div>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

            {filtered.length > pageSize && (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "10px 14px", ...text.caption }}>
                    <span>{(cur - 1) * pageSize + 1}–{Math.min(cur * pageSize, filtered.length)} of {filtered.length}</span>
                    <div style={{ display: "flex", gap: 6 }}>
                        <Button variant="secondary" size="sm" disabled={cur <= 1} onClick={() => setPage(cur - 1)}>Previous</Button>
                        <Button variant="secondary" size="sm" disabled={cur >= pages} onClick={() => setPage(cur + 1)}>Next</Button>
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── ExportMenu (CSV + Print now; Excel/PDF arrive with Accounts) ───────────
export function ExportMenu({ filename: baseName, columns, rows, title: baseTitle, provisional, note }) {
    // note: the reason a provisional export carries (default: the 2C opening-balances wording).
    const PROV = note || "PROVISIONAL – opening balances not posted";
    const filename = provisional ? `PROVISIONAL-${baseName}` : baseName;
    const title = provisional ? `${baseTitle} — ${PROV}` : baseTitle;
    const exportCols = columns.filter(c => c.exportValue);
    const toCSV = () => {
        const esc = (v) => { const s = v === null || v === undefined ? "" : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
        const lines = [...(provisional ? [esc(PROV)] : []), exportCols.map(c => esc(c.header)).join(","), ...rows.map(r => exportCols.map(c => esc(c.exportValue(r))).join(","))];
        const blob = new Blob(["﻿" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
        const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${filename}.csv`; a.click();
        setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    };
    const print = () => {
        const w = window.open("", "_blank");
        if (!w) return;
        const esc = (s) => String(s ?? "").replace(/[&<>]/g, ch => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[ch]));
        w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title>
<style>body{font-family:Segoe UI,Arial,sans-serif;color:#111;margin:24px}h1{font-size:18px;margin:0 0 4px}p{color:#555;font-size:12px;margin:0 0 16px}
table{border-collapse:collapse;width:100%;font-size:12px}th,td{border-bottom:1px solid #ddd;padding:6px 8px;text-align:left}th{background:#f4f5f7}</style></head>
<body><h1>${esc(title)}</h1><p>Foxbeep Technology Pvt. Ltd. · Generated ${new Date().toLocaleString()}</p>
<table><thead><tr>${exportCols.map(c => `<th>${esc(c.header)}</th>`).join("")}</tr></thead>
<tbody>${rows.map(r => `<tr>${exportCols.map(c => `<td>${esc(c.exportValue(r))}</td>`).join("")}</tr>`).join("")}</tbody></table></body></html>`);
        w.document.close(); w.focus(); w.print();
    };
    return <ActionMenu label="Export" items={[{ label: "Export CSV", onClick: toCSV }, { label: "Print", onClick: print }]} />;
}

// ─── DocumentUploader (camera on phones, image or PDF) ───────────────────────
// optimize: proofs / financial evidence only (never logos, stamps or QR codes) — the chosen image is optimised for upload
// (src/lib/uploadCompression.js) and both sizes are shown before anything is uploaded; the original is kept if that fails.
export function DocumentUploader({ file, onChange, accept = "image/*,application/pdf", maxMB = 10, label = "Payment proof", optimize = false }) {
    const mobile = useMobile();
    const [err, setErr] = useState("");
    const [preview, setPreview] = useState(null);
    const [info, setInfo] = useState(null), [busy, setBusy] = useState(false);
    const fileRef = useRef(null), camRef = useRef(null);
    useEffect(() => {
        if (!file || !file.type?.startsWith("image/")) { setPreview(null); return; }
        const url = URL.createObjectURL(file); setPreview(url);
        return () => URL.revokeObjectURL(url);
    }, [file]);
    useEffect(() => { if (!file) setInfo(null); }, [file]);
    const pick = async (f) => {
        setErr(""); setInfo(null);
        if (!f) return;
        let use = f;
        if (optimize) {
            setBusy(true);
            try { const r = await prepareUpload(f); if (r?.file) { use = r.file; setInfo(r); } } catch { /* keep the original */ }
            setBusy(false);
        }
        if (use.size > maxMB * 1024 * 1024) { setErr(`File is larger than ${maxMB} MB.`); return; }
        onChange(use);
    };
    return (
        <div>
            <span style={text.label}>{label}</span>
            {file ? (
                <div style={{ ...surface.inset, padding: 10, display: "flex", alignItems: "center", gap: 12 }}>
                    {preview ? <img src={preview} alt="" style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 6 }} />
                        : <div style={{ width: 48, height: 48, borderRadius: 6, background: T.surface, display: "grid", placeItems: "center", fontSize: 11, color: T.textMuted }}>PDF</div>}
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, color: T.textStrong, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.name}</div>
                        <div style={text.caption}>{(file.size / 1024).toFixed(0)} KB</div>
                    </div>
                    <Button variant="ghost" size="sm" type="button" onClick={() => onChange(null)}>Remove</Button>
                </div>
            ) : busy ? <OptimizeNote busy /> : (
                <div style={{ display: "flex", gap: 8, flexDirection: mobile ? "column" : "row" }}>
                    <button type="button" onClick={() => fileRef.current?.click()}
                        style={{ ...surface.inset, borderStyle: "dashed", flex: 1, padding: "14px 12px", color: T.textMuted, cursor: "pointer", fontSize: 13, fontFamily: T.font }}>
                        Choose image or PDF <span style={{ color: T.textFaint }}>(max {maxMB} MB)</span>
                    </button>
                    {mobile && <Button variant="secondary" type="button" onClick={() => camRef.current?.click()}>Take photo</Button>}
                </div>
            )}
            <input ref={fileRef} type="file" accept={accept} style={{ display: "none" }} onChange={e => { pick(e.target.files[0]); e.target.value = ""; }} />
            <input ref={camRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={e => { pick(e.target.files[0]); e.target.value = ""; }} />
            {file && optimize && <OptimizeNote info={info} />}
            {err && <div style={{ fontSize: 12, color: T.danger, marginTop: 4 }}>{err}</div>}
        </div>
    );
}

// ─── AuditHistory timeline ───────────────────────────────────────────────────
export function AuditHistory({ events }) {
    const list = events.filter(e => e && e.at);
    if (list.length === 0) return <div style={text.caption}>No history recorded.</div>;
    return (
        <ol style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 12 }}>
            {list.map((e, i) => (
                <li key={i} style={{ display: "flex", gap: 10 }}>
                    <span style={{ width: 8, height: 8, borderRadius: 4, marginTop: 6, background: e.tone || T.textFaint, flexShrink: 0 }} />
                    <div>
                        <div style={{ fontSize: 14, color: T.textStrong }}>{e.label}{e.by ? <span style={{ color: T.textMuted }}> by {e.by}</span> : null}</div>
                        <div style={text.caption}>{fmtDateTime(e.at)}</div>
                    </div>
                </li>
            ))}
        </ol>
    );
}

// ─── PaymentModal (shared by salary + bonus; Accounts later) ─────────────────
const nowLocal = () => { const d = new Date(); d.setMinutes(d.getMinutes() - d.getTimezoneOffset()); return d.toISOString().slice(0, 16); };

export function PaymentModal({ open, title, subtitle, summary, defaultAmount, amountHint, blocked, confirmLabel = "Confirm payment", methods, onConfirm, onClose }) {
    const [amount, setAmount] = useState("");
    const [paidAt, setPaidAt] = useState(nowLocal());
    const [method, setMethod] = useState("bank_transfer");
    const [txn, setTxn] = useState("");
    const [file, setFile] = useState(null);
    const [notes, setNotes] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        if (open) { setAmount(defaultAmount ?? ""); setPaidAt(nowLocal()); setMethod("bank_transfer"); setTxn(""); setFile(null); setNotes(""); setErr(""); }
    }, [open, defaultAmount]);

    const submit = async () => {
        setErr("");
        if (!(Number(amount) > 0)) { setErr("Enter the amount paid."); return; }
        if (!paidAt) { setErr("Choose the payment date."); return; }
        setBusy(true);
        try {
            await onConfirm({ amount: Number(amount), paidAt: new Date(paidAt).toISOString(), method, txn: txn.trim(), file, notes: notes.trim() });
        } catch (e) {
            setErr(e?.message || "Payment could not be recorded.");
        } finally { setBusy(false); }
    };

    return (
        <Modal open={open} title={title} subtitle={subtitle} onClose={busy ? undefined : onClose}
            footer={<>
                <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
                <Button variant="success" onClick={submit} disabled={busy || !!blocked}>{busy ? "Saving…" : confirmLabel}</Button>
            </>}>
            {summary && <div style={{ marginBottom: 20 }}>{summary}</div>}
            {blocked && <div style={{ marginBottom: 16 }}><Notice tone="warning">{blocked}</Notice></div>}
            <FormSection title="Payment">
                <Field label="Amount paid (NPR)" required hint={amountHint}>
                    <Input type="number" inputMode="decimal" min="0" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} />
                </Field>
                <Field label="Payment date" required>
                    <Input type="datetime-local" value={paidAt} onChange={e => setPaidAt(e.target.value)} />
                </Field>
                <Field label="Payment method" required>
                    <Select value={method} onChange={e => setMethod(e.target.value)}>
                        {methods.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </Select>
                </Field>
                <Field label="Transaction / reference number" hint={method === "cash" ? "Optional for cash" : undefined}>
                    <Input value={txn} onChange={e => setTxn(e.target.value)} placeholder="e.g. FT2610031234" />
                </Field>
            </FormSection>
            <FormSection title="Documents">
                <DocumentUploader file={file} onChange={setFile} optimize />
                <Field label="Notes"><TextArea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional" /></Field>
            </FormSection>
            {err && <Notice tone="danger">{err}</Notice>}
        </Modal>
    );
}
